import { memo, type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { WorkspaceAgentBeforeAfterTableScrollRegionRefProvider } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/tables/WorkspaceAgentBeforeAfterTable';

export type WorkspaceAgentChangePreviewPillTableHostProps = {
  children: ReactNode;
};

type ScrollAffordanceState = {
  atBottom: boolean;
  canScroll: boolean;
};

const INITIAL_SCROLL_AFFORDANCE: ScrollAffordanceState = {
  atBottom: true,
  canScroll: false,
};

export const WorkspaceAgentChangePreviewPillTableHost = memo(
  ({ children }: WorkspaceAgentChangePreviewPillTableHostProps) => {
    const [host, setHost] = useState<HTMLDivElement | null>(null);
    const [scrollRegion, setScrollRegion] = useState<HTMLElement | null>(null);
    const [scrollAffordance, setScrollAffordance] = useState(INITIAL_SCROLL_AFFORDANCE);

    useEffect(() => {
      if (!scrollRegion) {
        setScrollAffordance(INITIAL_SCROLL_AFFORDANCE);
        return;
      }

      const updateScrollAffordance = () => {
        const canScroll = scrollRegion.scrollHeight > scrollRegion.clientHeight;
        const atBottom = scrollRegion.scrollTop + scrollRegion.clientHeight >= scrollRegion.scrollHeight - 1;

        setScrollAffordance({ atBottom, canScroll });
      };

      updateScrollAffordance();

      scrollRegion.addEventListener('scroll', updateScrollAffordance, { passive: true });

      const resizeObserver = new ResizeObserver(updateScrollAffordance);
      resizeObserver.observe(scrollRegion);

      return () => {
        scrollRegion.removeEventListener('scroll', updateScrollAffordance);
        resizeObserver.disconnect();
      };
    }, [scrollRegion]);

    const showBottomFade = scrollAffordance.canScroll && !scrollAffordance.atBottom;

    return (
      <div
        className="[&_section]:scrollbar [&_section]:scrollbar-thumb-gray-7 [&_section:hover]:scrollbar-track-gray-2 [&_section:not(:hover)]:scrollbar-track-transparent relative min-w-0 [&_td:first-child]:w-[42%] [&_th:first-child]:w-[42%] [&_thead]:bg-gray-2 ads:[&_thead]:bg-raised"
        ref={setHost}
      >
        <WorkspaceAgentBeforeAfterTableScrollRegionRefProvider value={setScrollRegion}>
          {children}
        </WorkspaceAgentBeforeAfterTableScrollRegionRefProvider>
        {showBottomFade && scrollRegion && host
          ? createPortal(
              <div
                aria-hidden
                className="pointer-events-none sticky bottom-0 z-1 -mt-6 h-6 bg-gradient-to-t from-gray-2 to-transparent"
              />,
              scrollRegion,
            )
          : null}
      </div>
    );
  },
);

WorkspaceAgentChangePreviewPillTableHost.displayName = 'WorkspaceAgentChangePreviewPillTableHost';
